import React, { useState, useEffect, useCallback } from 'react';
import { get, put, post } from 'aws-amplify/api';
import OperatorSecurity from './OperatorSecurity';
import './OperatorConsole.css';

// 運営者用：全テナント横断でプラン変更・パスワード/MFAリセット・凍結を行う画面
const PLAN_LABELS = {
  free: '無料',
  active: '有料',
  trialing: '有料（無償期間）',
  past_due: '有料（支払い遅延）',
  canceled: '解約済み',
};

const formatDateTime = (v) => (v ? new Date(v).toLocaleString('ja-JP') : '—');
const formatDate = (v) => (v ? new Date(v).toLocaleDateString('ja-JP') : '—');

const extractErrorMessage = async (e, fallback) => {
  try { const b = await e.response.body.json(); return b.error || fallback; } catch { return fallback; }
};

function OperatorConsole({ currentUser, onBack }) {
  const [tab, setTab] = useState('tenants'); // tenants / security
  const [query, setQuery] = useState('');
  const [tenants, setTenants] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState('');

  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [planForm, setPlanForm] = useState({ plan: 'free', max_users: 10 });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success' | 'error', text }

  const fetchTenants = useCallback(async (q) => {
    setLoadingList(true);
    setListError('');
    try {
      const op = get({
        apiName: 'worktimerApi',
        path: '/tenants/operator/tenants',
        options: q ? { queryParams: { q } } : undefined,
      });
      const { body } = await op.response;
      setTenants(await body.json());
    } catch (e) {
      const msg = await extractErrorMessage(e, 'テナント一覧の取得に失敗しました');
      setListError(msg === 'operator_mfa_required'
        ? '運営管理画面を使うには、MFA（多要素認証）の設定が必要です。マイプロフィールから設定してください。'
        : msg);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { fetchTenants(''); }, [fetchTenants]);

  const openTenant = async (tenantId) => {
    setLoadingDetail(true);
    setMessage(null);
    try {
      const op = get({ apiName: 'worktimerApi', path: `/tenants/operator/tenants/${encodeURIComponent(tenantId)}` });
      const { body } = await op.response;
      const data = await body.json();
      setDetail(data);
      setPlanForm({
        plan: data.subscription_status === 'free' ? 'free' : 'paid',
        max_users: data.subscription_status === 'free' ? 10 : data.max_users,
      });
    } catch (e) {
      setMessage({ type: 'error', text: await extractErrorMessage(e, 'テナント情報の取得に失敗しました') });
    } finally {
      setLoadingDetail(false);
    }
  };

  const refreshDetail = async () => {
    if (detail) await openTenant(detail.tenant_id);
    fetchTenants(query.trim());
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTenants(query.trim());
  };

  // ===== プラン変更 =====
  const handleSavePlan = async () => {
    const isFree = planForm.plan === 'free';
    const maxUsers = isFree ? 3 : parseInt(planForm.max_users, 10);
    if (!isFree && (!Number.isInteger(maxUsers) || maxUsers < 1)) {
      setMessage({ type: 'error', text: 'ユーザー上限を正しく入力してください' });
      return;
    }
    const lines = [
      `「${detail.company_name || detail.tenant_name || detail.tenant_id}」のプランを変更します。`,
      `プラン: ${PLAN_LABELS[detail.subscription_status] || detail.subscription_status} → ${isFree ? '無料' : '有料'}`,
      `ユーザー上限: ${detail.max_users}人 → ${maxUsers}人`,
    ];
    if (maxUsers < detail.users.length) {
      lines.push('', `⚠️ 現在のユーザー数（${detail.users.length}人）が上限を超えます。既存ユーザーはそのまま使えますが、新しいユーザーは追加できなくなります。`);
    }
    if (isFree) lines.push('', '※ 無料プランではタスクを新規作成できるのは合計3件までになります。');
    if (!window.confirm(lines.join('\n'))) return;

    setBusy(true);
    setMessage(null);
    try {
      const op = put({
        apiName: 'worktimerApi',
        path: `/tenants/operator/tenants/${encodeURIComponent(detail.tenant_id)}/plan`,
        options: { body: { plan: planForm.plan, max_users: maxUsers } },
      });
      await op.response;
      setMessage({ type: 'success', text: 'プランを変更しました' });
      await refreshDetail();
    } catch (e) {
      setMessage({ type: 'error', text: await extractErrorMessage(e, 'プランの変更に失敗しました') });
    } finally {
      setBusy(false);
    }
  };

  // ===== ユーザー操作 =====
  const userLabel = (u) => `${u.display_name || u.username}（${u.email || 'メール未登録'}）`;

  const handleResetPassword = async (u) => {
    if (!window.confirm(`${userLabel(u)} のパスワードをリセットします。\n\n仮パスワードがユーザーのメールアドレスに送信され、次回ログイン時にパスワードの再設定が求められます。現在のパスワードは使えなくなります。`)) return;
    setBusy(true);
    setMessage(null);
    try {
      const op = post({ apiName: 'worktimerApi', path: `/tenants/operator/users/${encodeURIComponent(u.user_id)}/reset-password` });
      const { body } = await op.response;
      const data = await body.json();
      if (data.email_sent) {
        setMessage({ type: 'success', text: `${u.display_name || u.username} さんに仮パスワードをメールで送信しました` });
      } else {
        // メール送信に失敗した場合のみ仮パスワードが返る。本人確認のうえ直接伝える
        setMessage({
          type: 'error',
          text: `パスワードはリセットしましたが、メール送信に失敗しました。本人確認のうえ、次の仮パスワードを直接お伝えください：${data.temporary_password}`,
        });
      }
    } catch (e) {
      setMessage({ type: 'error', text: await extractErrorMessage(e, 'パスワードのリセットに失敗しました') });
    } finally {
      setBusy(false);
    }
  };

  const handleResetMfa = async (u) => {
    if (!window.confirm(`${userLabel(u)} のMFA（多要素認証）をリセットします。\n\n認証アプリの登録が解除され、次回ログイン時に改めてMFAの設定が必要になります。`)) return;
    setBusy(true);
    setMessage(null);
    try {
      const op = post({ apiName: 'worktimerApi', path: `/tenants/operator/users/${encodeURIComponent(u.user_id)}/reset-mfa` });
      const { body } = await op.response;
      const data = await body.json();
      setMessage({
        type: 'success',
        text: data.note === 'admin_webauthn_not_revocable'
          ? 'MFAをリセットしました。※管理者のパスキー（生体認証など）はシステムの仕様上削除できないため、登録済みのパスキーは引き続き使えます。'
          : 'MFAをリセットしました。次回ログイン時に再設定が求められます。',
      });
      await refreshDetail();
    } catch (e) {
      setMessage({ type: 'error', text: await extractErrorMessage(e, 'MFAのリセットに失敗しました') });
    } finally {
      setBusy(false);
    }
  };

  const handleToggleFreeze = async (u) => {
    const freezing = !u.is_frozen;
    if (!window.confirm(`${userLabel(u)} を${freezing ? '凍結' : '凍結解除'}します。${freezing ? '\n\n凍結するとこのユーザーはログインできなくなります。' : ''}`)) return;
    setBusy(true);
    setMessage(null);
    try {
      const op = put({
        apiName: 'worktimerApi',
        path: `/tenants/operator/users/${encodeURIComponent(u.user_id)}/freeze`,
        options: { body: { is_frozen: freezing } },
      });
      await op.response;
      setMessage({ type: 'success', text: `${u.display_name || u.username} さんを${freezing ? '凍結' : '凍結解除'}しました` });
      await refreshDetail();
    } catch (e) {
      setMessage({ type: 'error', text: await extractErrorMessage(e, '操作に失敗しました') });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="opc">
      <div className="opc-header">
        <button className="opc-back" onClick={onBack}>← 戻る</button>
        <div>
          <h2>🛠 運営管理</h2>
          <p>全テナントのプラン変更、パスワード・MFAのリセット、セキュリティの確認ができます。操作内容は各テナントの変更履歴に「運営（FACTime）」として記録されます。</p>
        </div>
      </div>

      <div className="opc-tabs">
        <button className={`opc-tab ${tab === 'tenants' ? 'active' : ''}`} onClick={() => setTab('tenants')}>🏢 テナント</button>
        <button className={`opc-tab ${tab === 'security' ? 'active' : ''}`} onClick={() => { setTab('security'); setMessage(null); }}>🔍 セキュリティ</button>
      </div>

      {message && (
        <div className={`opc-message opc-message-${message.type}`}>
          {message.text}
          <button className="opc-message-close" onClick={() => setMessage(null)} aria-label="閉じる">✕</button>
        </div>
      )}

      {tab === 'security' ? (
        <div className="opc-card">
          <OperatorSecurity onOpenTenant={(id) => { setTab('tenants'); openTenant(id); }} />
        </div>
      ) : detail ? (
        <div className="opc-card">
          <div className="opc-detail-header">
            <button className="opc-link" onClick={() => { setDetail(null); setMessage(null); }}>← テナント一覧へ</button>
            <h3>{detail.company_name || detail.tenant_name || '(会社名未登録)'}</h3>
            <div className="opc-meta">
              テナントID: {detail.tenant_id} ／ 登録日: {formatDate(detail.created_at)}
              {detail.email && ` ／ ${detail.email}`}
              {detail.phone && ` ／ ☎ ${detail.phone}`}
            </div>
          </div>

          <section className="opc-section">
            <h4>プラン</h4>
            <div className="opc-plan-now">
              現在：<span className={`opc-badge opc-badge-${detail.subscription_status}`}>{PLAN_LABELS[detail.subscription_status] || detail.subscription_status}</span>
              ユーザー {detail.users.length} / {detail.max_users} 人 ・ タスク {detail.task_count} 件
            </div>
            <div className="opc-plan-form">
              <label>
                <input type="radio" name="plan" checked={planForm.plan === 'free'} onChange={() => setPlanForm((f) => ({ ...f, plan: 'free' }))} disabled={busy} />
                無料プラン（ユーザー3人・タスク3件まで）
              </label>
              <label>
                <input type="radio" name="plan" checked={planForm.plan === 'paid'} onChange={() => setPlanForm((f) => ({ ...f, plan: 'paid' }))} disabled={busy} />
                有料プラン（タスク無制限）
              </label>
              {planForm.plan === 'paid' && (
                <label className="opc-max-users">
                  ユーザー上限
                  <input
                    type="number"
                    min={1}
                    max={10000}
                    value={planForm.max_users}
                    onChange={(e) => setPlanForm((f) => ({ ...f, max_users: e.target.value }))}
                    disabled={busy}
                  />
                  人
                </label>
              )}
              <button className="opc-btn-primary" onClick={handleSavePlan} disabled={busy}>プランを変更</button>
            </div>
          </section>

          <section className="opc-section">
            <h4>ユーザー（{detail.users.length}人）</h4>
            <div className="opc-table-wrap">
              <table className="opc-table">
                <thead>
                  <tr>
                    <th>ユーザー</th>
                    <th>権限</th>
                    <th>MFA</th>
                    <th>状態</th>
                    <th>最終ログイン</th>
                    <th className="opc-col-actions">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.users.map((u) => {
                    const isSelf = currentUser?.user_id === u.user_id;
                    return (
                      <tr key={u.user_id} className={u.is_frozen ? 'opc-row-frozen' : ''}>
                        <td>
                          <div className="opc-user-name">{u.display_name || u.username}{isSelf && '（あなた）'}</div>
                          <div className="opc-user-sub">{u.username} ／ {u.email || 'メール未登録'}</div>
                        </td>
                        <td>{u.role === 'admin' ? '管理者' : '一般'}</td>
                        <td>{u.mfa_enabled ? (u.mfa_type === 'webauthn' ? 'パスキー' : '認証アプリ') : '未設定'}</td>
                        <td>{u.is_frozen ? <span className="opc-badge opc-badge-frozen">凍結中</span> : '有効'}</td>
                        <td>{formatDateTime(u.last_login_at)}</td>
                        <td className="opc-col-actions">
                          <button className="opc-btn-small" onClick={() => handleResetPassword(u)} disabled={busy}>パスワード再発行</button>
                          <button className="opc-btn-small" onClick={() => handleResetMfa(u)} disabled={busy || isSelf || !u.mfa_enabled}>MFAリセット</button>
                          <button className="opc-btn-small opc-btn-danger" onClick={() => handleToggleFreeze(u)} disabled={busy || isSelf}>
                            {u.is_frozen ? '凍結解除' : '凍結'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      ) : (
        <div className="opc-card">
          <form className="opc-search" onSubmit={handleSearch}>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="会社名・テナントID・ユーザー名・メールアドレスで検索"
            />
            <button className="opc-btn-primary" type="submit" disabled={loadingList}>検索</button>
          </form>

          {listError ? (
            <div className="opc-message opc-message-error">{listError}</div>
          ) : loadingList || loadingDetail ? (
            <div className="opc-empty">読み込み中…</div>
          ) : tenants.length === 0 ? (
            <div className="opc-empty">該当するテナントがありません</div>
          ) : (
            <div className="opc-table-wrap">
              <table className="opc-table">
                <thead>
                  <tr>
                    <th>会社名</th>
                    <th>プラン</th>
                    <th>ユーザー</th>
                    <th>登録日</th>
                    <th>最終ログイン</th>
                  </tr>
                </thead>
                <tbody>
                  {tenants.map((t) => (
                    <tr key={t.tenant_id} className="opc-row-link" onClick={() => openTenant(t.tenant_id)}>
                      <td>
                        <div className="opc-user-name">{t.company_name || t.tenant_name || '(会社名未登録)'}</div>
                        <div className="opc-user-sub">{t.tenant_id}{t.email && ` ／ ${t.email}`}</div>
                      </td>
                      <td><span className={`opc-badge opc-badge-${t.subscription_status}`}>{PLAN_LABELS[t.subscription_status] || t.subscription_status}</span></td>
                      <td>{t.user_count} / {t.max_users} 人</td>
                      <td>{formatDate(t.created_at)}</td>
                      <td>{formatDateTime(t.last_login_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default OperatorConsole;
